import { TFunction } from 'i18next';
import {
  NewFormEntity,
  DataFieldCols,
  ProcessFormAcceptedCommodity,
  ProcessFormAddressAcceptedValues,
  ProcessFormAddressAcceptedValuesWithDescription,
  ProcessFormNewFormFieldOverride,
  ProcessFormSelectOption,
} from 'types/dataCaptureTypes';
import { TreeNode } from './ConfigurationForm/configs/types';
import {
  getLeafNodesForValue,
  parseProductTreeData,
  parseLocationTreeData,
  PRODUCT_FIELD,
  PROCESS_DESCRIPTION_FIELD,
  COOLING_RECIPIENT_LOCATION_DESCRIPTION,
  LOCATION_FIELD_LIST,
} from './Tabs/FormCapture/columns';

const buildTreeFieldOverride = (
  field: DataFieldCols,
  treeData: TreeNode[]
): {
  defaultValue: NewFormEntity | undefined | null;
  acceptedValues: NewFormEntity[];
} => {
  let defaultValue: NewFormEntity | undefined | null = undefined;

  if (field.defaultValue) {
    const defaultLeaves = getLeafNodesForValue(treeData, field.defaultValue);
    if (defaultLeaves.length > 0) {
      defaultValue = {
        entityId: defaultLeaves[0].value,
        value: defaultLeaves[0].title,
      };
    }
  } else if (field.default_values?.value) {
    defaultValue = null;
  }

  let acceptedFormatted: NewFormEntity[] = [];
  if (field.acceptedValues.length > 0) {
    field.acceptedValues.forEach((selectedId: string) => {
      const leaves = getLeafNodesForValue(treeData, selectedId);
      acceptedFormatted.push(
        ...leaves.map(leaf => ({
          entityId: leaf.value,
          value: leaf.title,
        }))
      );
    });
    acceptedFormatted = acceptedFormatted.filter(
      (option, index, self) =>
        index === self.findIndex(o => o.entityId === option.entityId)
    );
  }

  return { defaultValue, acceptedValues: acceptedFormatted };
};

/** Builds override values for standard select fields */
const buildStandardSelectOverride = (field: DataFieldCols) => {
  let defaultValue: NewFormEntity | undefined | null = undefined;
  if (field.defaultValue) {
    const found = (
      field.accepted_values.value as ProcessFormSelectOption[]
    ).find(opt => opt.id === field.defaultValue);
    if (found) {
      defaultValue = { entityId: found.id, value: found.name };
    }
  } else if (field.default_values?.value) {
    defaultValue = { value: null };
  }
  const acceptedArr = field.acceptedValues.length ? field.acceptedValues : [];
  const acceptedOptions = (
    field.accepted_values.value as ProcessFormSelectOption[]
  ).filter(opt => acceptedArr.includes(opt.id));
  const acceptedValues = acceptedOptions.map(opt => ({
    entityId: opt.id,
    value: opt.name,
  }));
  return { defaultValue, acceptedValues };
};

/** Determines the field override based on field type and requirements */
export const getFieldOverride = (
  field: DataFieldCols,
  t: TFunction
): ProcessFormNewFormFieldOverride => {
  const baseOverride: ProcessFormNewFormFieldOverride = {
    ...(field.is_custom
      ? { customFieldId: field.key }
      : { templateFieldId: field.key }),
    isRequired: field.required.value,
    isVisible: field.visible.value,
    isFlowThrough: field.flowThrough.value,
  };

  if (
    [
      PRODUCT_FIELD,
      PROCESS_DESCRIPTION_FIELD,
      COOLING_RECIPIENT_LOCATION_DESCRIPTION,
      ...LOCATION_FIELD_LIST,
    ].includes(field.name)
  ) {
    const treeData =
      field.name === PRODUCT_FIELD
        ? parseProductTreeData(
            field.accepted_values.value as ProcessFormAcceptedCommodity[],
            t
          )
        : parseLocationTreeData(
            field.accepted_values.value as
              | ProcessFormAddressAcceptedValues
              | ProcessFormAddressAcceptedValuesWithDescription
          );
    const { defaultValue, acceptedValues } = buildTreeFieldOverride(
      field,
      treeData
    );
    return { ...baseOverride, defaultValue, acceptedValues };
  }
  if (
    field.field_type === 'Select' &&
    Array.isArray(field.accepted_values.value)
  ) {
    const { defaultValue, acceptedValues } = buildStandardSelectOverride(field);
    return { ...baseOverride, defaultValue, acceptedValues };
  }
  return {
    ...baseOverride,
    ...(field.defaultValue
      ? { defaultValue: { value: field.defaultValue } }
      : field.default_values?.value
      ? { defaultValue: { value: null } }
      : {}),
  };
};
